import { useLayoutEffect, useRef, useState } from 'react';
import lottie from 'lottie-web';

/**
 * Lottie player using CANVAS renderer for embedded PNG image sequences.
 * Uses useLayoutEffect + requestAnimationFrame to guarantee the container
 * DOM element is painted and has dimensions before lottie.loadAnimation() runs.
 * This prevents the "CVImageElement.createContent null width" crash.
 */
export default function LottiePlayer({
  src, loop = true, autoplay = true, speed = 1,
  style = {}, testId, onReady, preserveAspectRatio = 'xMidYMid slice',
}) {
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Phase 1: wait for DOM paint
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setReady(true));
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Phase 2: load animation once container is painted and has size
  useLayoutEffect(() => {
    if (!ready || !containerRef.current) return;
    const el = containerRef.current;
    if (el.offsetWidth === 0 && el.offsetHeight === 0) return;

    if (animRef.current) {
      animRef.current.destroy();
      animRef.current = null;
    }

    try {
      const anim = lottie.loadAnimation({
        container: el,
        renderer: 'canvas',
        loop,
        autoplay,
        path: src,
        rendererSettings: {
          preserveAspectRatio,
          clearCanvas: true,
          progressiveLoad: true,
        },
      });
      anim.setSpeed(speed);
      animRef.current = anim;
      if (onReady) onReady();
    } catch (e) {
      console.warn('Lottie load error:', src, e);
    }

    return () => {
      if (animRef.current) {
        try { animRef.current.destroy(); } catch (e) {}
        animRef.current = null;
      }
    };
  }, [ready, src, loop, autoplay, preserveAspectRatio]);

  // Update speed dynamically
  useLayoutEffect(() => {
    if (animRef.current) {
      try { animRef.current.setSpeed(speed); } catch (e) {}
    }
  }, [speed]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      style={{ pointerEvents: 'none', minWidth: 2, minHeight: 2, ...style }}
    />
  );
}
